import React, { Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import LazyLoad from 'react-lazyload';
import Breadcrumb from '@deporvillage/breadcrumb-component';
import BundledProducts from '@deporvillage/bundled-products-component';
import Clearpay, { ClearpayType } from '@deporvillage/clearpay-component';
import HubtypeWebchat from '@deporvillage/hubtype-webchat-component';
import LinkList from '@deporvillage/link-list-component';
import Modal from '@deporvillage/modal-component';
import PayPalWidget from '@deporvillage/paypal-widget-component';
import Picture from '@deporvillage/picture-component';
import ProductActions, { ProductActionVariantClick } from '@deporvillage/product-actions-component';
import ProductGallery, { ProductGalleryClick } from '@deporvillage/product-gallery-component';
import ProductSlider from '@deporvillage/product-slider-component';
import ReviewPopup, { ReviewPopupType } from '@deporvillage/review-popup-component';
import Stars from '@deporvillage/stars-component';
import TagList from '@deporvillage/tag-list-component';
import AdManager, { AdManagerId, init as InitAdManager } from '@deporvillage/ad-manager-component';
import { initialize as initializeTracking, track } from '@deporvillage/tracking-library-component';
import {
    Config,
    formatPrice,
    I18n,
    I18nContext,
    DeviceType,
    getQueryParams,
    getCookie,
    StockLabel,
} from '@deporvillage/utils-library-component';
import { CartContext } from '@deporvillage/frontend-common/src/context/cart/context';
import { GetCartItems, SetTotalItems } from '@deporvillage/frontend-common/src/context/cart/actions';
import { trackEeViewItem, trackEeViewItemList, trackEeAddToCart } from '@deporvillage/frontend-common';
import ProductPageTabs, { ProductTabType } from './Tabs';
import CartError from './CartError';
import OutOfStockModal from './OutOfStockModal';

import styles from './Product.module.scss';

const REVIEWS_PAGE_LIMIT = 15;
const REVIEWS_RELATED_PAGE_LIMIT = 15;

const OutOfStockModalLimitItems = 8;
const OutOfStockModalTimeoutMs = 8000;

const SliderBreakpoints = {
    0: {
        slidesPerView: 2.5,
        slidesPerGroup: 1,
        spaceBetween: 0,
        freeMode: true,
    },
    575: {
        slidesPerView: 3.5,
        slidesPerGroup: 1,
        spaceBetween: 0,
        freeMode: true,
    },
    1034: {
        slidesPerView: 6,
        slidesPerGroup: 6,
        spaceBetween: 0,
        freeMode: false,
    },
    1370: {
        slidesPerView: 7,
        slidesPerGroup: 7,
        spaceBetween: 0,
        freeMode: false,
    },
};

/* istanbul ignore next */
const getBasePrice = (price, store) => {
    const factorCurrency = store.currency_conversion_factor || 1;
    const factorPrice = store.price_factor || 1;
    return +(price * factorPrice * factorCurrency).toFixed(2);
};

// toDo: price bot FF
const priceModifier = ({ threshold, value, isBot }) => (
    !!isBot * value * (1 + threshold / 100) || value
);

const mapProductForTracking = ({
    id, title, sku, brand, rating, defaultVariant, mainCategory,
}, store) => ({
    id,
    sku,
    name: title,
    replenishment: !!defaultVariant?.replenishment,
    stock: +(defaultVariant?.stockLabel !== StockLabel.NoStock),
    price: {
        price: defaultVariant?.price?.price,
        pvp: defaultVariant?.price?.pvp,
        base: getBasePrice(defaultVariant?.price?.price, store),
    },
    category: {
        id: mainCategory?.id?.id,
        name: mainCategory?.name,
    },
    brand: {
        id: brand?.id,
        name: brand?.name,
    },
    reviews: {
        rate: rating?.rate,
        count: rating?.count,
    },
});

/* istanbul ignore next */
const mapForDataLayerTracking = (items, categories) => items?.map((i) => ({
    title: i?.title,
    sku: i?.sku,
    price: i?.default_variant?.price?.price,
    isNew: i?.isNew,
    isClearance: i?.isClearance,
    categories,
}));

/* istanbul ignore next */
const scrollToTop = () =>
    window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
    });

/* istanbul ignore next */
const showClearPay = ({
    enabled, min, max, includeCategories, excludeCategories,
}, ids, price) => {
    const e = (ids || []).filter((c) => (excludeCategories || []).includes(c));
    const i = (ids || []).filter((c) => (includeCategories || []).includes(c));
    if ((!enabled || price < min || price > max || e.length) && !i.length) return false;
    return true;
};

// istanbul ignore next
const fetchSimilarProducts = async ({
    api, lang, country, id,
}) => {
    try {
        const ans = await fetch(
            `${api}/api/recommendation/product/similar-products/${lang}/${country}?id=${id}`,
            { priority: 'low' },
        );
        const res = await ans.json();
        if (!res?.items?.length) return null;
        return res.items.filter((i) => i).slice(0, OutOfStockModalLimitItems);
    } catch (e) {
        return null;
    }
};

const ProductPage = (props) => {
    const {
        bike_mount: bikeMount, blocks, brand, breadcrumb, default_variant: defaultVariant,
        description, discount_tag: discountTag, expert_opinion: expertOpinion,
        gallery_images: gallery, uuid, id, has_packs: hasPacks, main_category: mainCategory,
        non_returnable: nonReturnable, related_products: relatedProducts, rating,
        root_category: rootCategory, sku, similar_brands: similarBrands, sustainability, tags,
        technologies, title, url, variants, categoryIds, is_new: isNew, isClearance,
    } = props.model;

    const [reviews, setReviews] = React.useState(props.model.reviews);
    const [reviewsRelated, setReviewsRelated] = React.useState(props.model.reviewsRelated);
    const [blockBrandProducts, setBlockBrandProducts] = React.useState(null);
    const [blockLastViews, setBlockLastViews] = React.useState(null);
    const [addedToCart, setAddedToCart] = React.useState(false);
    const [bundleAddedToCart, setBundleAddedToCart] = React.useState(false);
    const [user, setUser] = React.useState({});
    const [tab, setTab] = React.useState(1);
    const [packs, setPacks] = React.useState([]);
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [isWishListActive, setIsWishListActive] = React.useState(false);
    const [delivery, setDelivery] = React.useState(null);
    const [promotion, setPromotion] = React.useState(false);
    const [promotionRequested, setPromotionRequested] = React.useState(false);
    const [cartErrors, setCartErrors] = React.useState(null);
    const [showModalAddToCart, setShowModalAddToCart] = React.useState(false);
    const [showAddReview, setShowAddReview] = React.useState(false);
    const [cartTimeoutId, setCartTimeoutId] = React.useState(null);
    const [cartButtonTimeoutId, setCartButtonTimeoutId] = React.useState(null);
    const [cartResponse, setCartResponse] = React.useState(null);
    const [outOfStockModal, setOutOfStockModal] = React.useState(null);
    const [, dispatch] = useContext(CartContext);

    const elemTabContentWrapper = React.useRef(null);

    const { translate } = React.useContext(I18nContext);
    const {
        tracking, debug, api, lang, country, store, showPercentDiscount, device,
        canRunAds, ad, isMobile, url: confUrl, hubtypeWebchat, paypal, isOptanonInited, clearPay,
        isBot, featureFlags,
    } = React.useContext(Config);

    const price = {
        pvp: priceModifier({
            threshold: featureFlags?.enableBotPrice?.threshold,
            value: defaultVariant?.price?.pvp,
            isBot,
        }),
        price: priceModifier({
            threshold: featureFlags?.enableBotPrice?.threshold,
            value: defaultVariant?.price?.price,
            isBot,
        }),
        currency: defaultVariant?.price?.currency_code,
    };

    const storeId = store.associated_store;
    const discountAmount = Math.round(100 * ((price.pvp - price.price) / price.pvp));
    const showPvpr = discountAmount >= 10 && price.pvp !== price.price;
    /* istanbul ignore next */
    const relatedProductsUuids = relatedProducts ? relatedProducts[0].items.map((i) => i.uuid) : [];
    const outOfStock = defaultVariant.stockLabel === StockLabel.NoStock &&
        !defaultVariant.replenishment;

    const isValidCurrency = country.currency_code === 'EUR';

    /* istanbul ignore next */
    const breadcrumbItems = [{
        url: '/',
        name: translate('generic.home'),
    }, /* istanbul ignore next */ ...(breadcrumb || [])].map((i, k, a) => (
        { ...i, hidden: isMobile && !(/* istanbul ignore next */ a.length - 3 < k) }
    ));

    const categories = breadcrumb?.reduce((p, { name }) => [...p, name], []);

    React.useEffect(() => {
        if (canRunAds) {
            InitAdManager([
                { ...ad.promo, isPriorityBanner: true },
                isMobile ? ad.middleMobile : ad.middle,
                isMobile ? ad.footerMobile : ad.footer,
            ], lang);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [canRunAds]);

    React.useEffect(() => {
        // eslint-disable-next-line no-undef
        const { opt, skey, rtm } = getQueryParams(window.location.href);
        initializeTracking({
            ...tracking,
            page: 'product',
            debug,
        });

        track({
            type: 'SET',
            name: 'setPage',
            data: {
                page: 'product',
                product: mapProductForTracking({
                    id,
                    title,
                    sku,
                    defaultVariant,
                    mainCategory,
                    brand,
                    rating,
                }, country),
                category: {
                    root_id: rootCategory,
                },
                opt,
                skey,
                rtm,
            },
        });

        track({ type: 'ACTION', name: 'VISIT' });
        track({ type: 'ACTION', name: 'PRODUCT_VIEW' });

        if (featureFlags?.enableGtmGa4) {
            trackEeViewItem({
                item: {
                    sku,
                    title,
                    brand: brand?.name,
                    categories,
                    isNew,
                    isClearance,
                    price: price?.price,
                },
            });

            trackEeViewItemList({
                listName: 'product-slider-affinity_product',
                listId: 'product-slider-affinity',
                items: mapForDataLayerTracking(blocks?.productAffinityProducts?.items),
            });

            trackEeViewItemList({
                listName: 'top-sellers_product',
                listId: 'top-sellers',
                items: mapForDataLayerTracking(blocks?.categoryTopSellers?.items),
            });
        }

        /* istanbul ignore next */
        if (!variants.length && !outOfStock) {
            const { stockLabel } = defaultVariant;
            fetchDeliveryTime({
                stockLabel,
                replenishment: defaultVariant.replenishment,
                uuid,
            });
        }

        if (hasPacks && !outOfStock) {
            /* istanbul ignore next */
            fetch(`${api}/api/product/packs?lang=${lang || 'es'}&country=${country.code || 'ES'}&id=${id}`, { priority: 'low' })
                /* istanbul ignore next */
                .then((ans) => ans.json())
                /* istanbul ignore next */
                .catch(() => { })
                /* istanbul ignore next */
                .then((ans) => {
                    if (ans) {
                        let packsFiltered = ans.filter((p) => p.length)
                            .map((p) => [
                                { ...props.model, checked: true },
                                ...p.filter((f) => (
                                    f.default_variant.stockLabel !== StockLabel.NoStock ||
                                    f.default_variant.replenishment
                                ))
                                    // eslint-disable-next-line max-len
                                    .map((i) => ({ ...i, checked: true })),
                            ]);

                        // @toDo FeatureFlag
                        if (featureFlags?.enableNewAddToCart) {
                            packsFiltered = ans.filter((p) => p.length)
                                .map((p) => [
                                    { ...props.model, checked: true },
                                    ...p.filter((f) => (
                                        f.default_variant.stockLabel !== StockLabel.NoStock ||
                                        f.default_variant.replenishment
                                    ))
                                        // eslint-disable-next-line max-len
                                        .map((i) => ({ ...i, checked: true, uuid: i.default_variant.uuid })),
                                ]);
                        }

                        setPacks(packsFiltered);
                    }
                })
                /* istanbul ignore next */
                .catch(() => { });
        }

        /* eslint-disable no-undef */
        /* istanbul ignore next */
        fetch(`${api}/api/product/block/brand?lang=${lang || 'es'}&country=${country.code || 'ES'}&id=${id}`, { priority: 'low' })
            /* istanbul ignore next */
            .then((ans) => ans.json())
            /* istanbul ignore next */
            .catch(() => { })
            /* istanbul ignore next */
            .then((ans) => {
                if (ans) {
                    setBlockBrandProducts({
                        translation: ans.translation,
                        items: ans.items.map((i) => ({
                            ...i,
                            related: null,
                        })),
                    });

                    if (featureFlags?.enableGtmGa4) {
                        trackEeViewItemList({
                            listName: 'product-slider-brand_product',
                            listId: 'product-slider-brand',
                            items: mapForDataLayerTracking(ans?.items),
                        });
                    }
                }
            })
            /* istanbul ignore next */
            .catch(() => { });
        /* eslint-enable no-undef */

        /* istanbul ignore next */
        /* eslint-disable no-undef */
        window.dispatchEvent(new CustomEvent('user:getdata'));
        window.addEventListener('user:data', handleUserData);

        return () => {
            window.removeEventListener('user:data', handleUserData);
        };
        /* eslint-enable no-undef */
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    /* istanbul ignore next */
    const handleWishListUpdate = React.useCallback(({ detail }) =>
        setIsWishListActive(detail.isSaved), [setIsWishListActive]);

    React.useEffect(() => {
        /* eslint-disable no-undef */
        window.addEventListener('wishlist:data', handleWishListUpdate);
        window.addEventListener('wishlist:update', handleWishListUpdate);
        return () => {
            window.removeEventListener('wishlist:data', handleWishListUpdate);
            window.removeEventListener('wishlist:update', handleWishListUpdate);
        };
        /* eslint-enable no-undef */
    }, [handleWishListUpdate]);

    React.useEffect(() => {
        const trackingId = getCookie('dpvUid');
        /* istanbul ignore next */
        if (trackingId) {
            // TODO test blocks fetching
            /* eslint-disable-next-line no-undef */
            fetch(`${api}/recommendation/${trackingId}/last-views-products/${lang}/${country.code}?product_id=${id}&${Date.now()}`, { priority: 'low' })
                /* istanbul ignore next */
                .then((ans) => ans.json())
                .catch(() => { })
                /* istanbul ignore next */
                .then((ans) => {
                    if (ans) {
                        setBlockLastViews(ans);

                        if (featureFlags?.enableGtmGa4) {
                            trackEeViewItemList({
                                listName: 'product-slider-lv_product',
                                listId: 'product-slider-lv',
                                items: mapForDataLayerTracking(ans?.items),
                            });
                        }
                    }
                })
                /* istanbul ignore next */
                .catch(() => { });
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    // istanbul ignore next
    const handleOutOfStockModal = async () => {
        const items = await fetchSimilarProducts({
            api, lang, country: country.code, id,
        });

        const tId = setTimeout(() => {
            window.removeEventListener('scroll', handleOnScroll);
            if (!document?.querySelector('[class*=modal-component]')) {
                setOutOfStockModal(items);
            }
        }, OutOfStockModalTimeoutMs);

        const handleOnScroll = () => {
            clearTimeout(tId);
            window.removeEventListener('scroll', handleOnScroll);
            if (!document?.querySelector('[class*=modal-component]')) {
                setOutOfStockModal(items);
            }
        };

        setTimeout(() => window.addEventListener('scroll', handleOnScroll), 1000);
    };

    React.useEffect(() => {
        if (outOfStock) handleOutOfStockModal();
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);

    /* istanbul ignore next */
    const isArrowHidden = () => (blockLastViews?.items?.length <= 7);

    /* istanbul ignore next */
    const handleUserData = (obj) => {
        setUser({
            sessionId: obj.detail.session_id,
            userId: obj.detail.customer_id,
            email: obj.detail.email,
            isLogged: obj.detail.logged_in,
        });
        /* eslint-disable-next-line no-undef */
        if (!obj.detail.logged_in) return;
        window.removeEventListener('user:data', handleUserData);
        window.dispatchEvent(new CustomEvent('wishlist:get', { detail: { id } }));
        const { review } = getQueryParams(window.location.href);
        if (review) setShowAddReview(true);
    };

    /* istanbul ignore next */
    const fetchDeliveryTime = async ({ stockLabel, replenishment, uuid }) => {
        const cartAmount = getBasePrice(price.price, country);
        const stock = +(stockLabel !== StockLabel.NoStock);
        const brandName = (brand && brand.name.toLowerCase()) || '';
        const categories = categoryIds.join('|');

        // TODO: remove unecessary query params when warehouse 2 is active
        const urlParams = [
            `country=${country.code}`,
            `categories=${categories}`,
            `cart-amount=${cartAmount}`,
            `has-stock=${stock}`,
            `replenishable=${+replenishment}`,
            `products-id=${uuid}`,
            'products-qty=1',
            `brands=${brandName}`,
        ].join('&');

        try {
            const ans = await fetch(`${api}/shipping-methods/nearest-delivery-date?${urlParams}`);
            const res = await ans.json();
            setDelivery({
                stockLabel,
                range: res?.delivery_date_range || null,
            });
        } catch (err) {
            // nothing to do
        }
    };

    const itemToCart = (obj) => {
        updateCart(obj);

        track({
            type: 'ACTION',
            name: 'GENERIC_CLICK',
            action: 'Añadir a carrito',
            label: 'Click en boton Añadir al carrito',
        });

        track({
            type: 'ACTION',
            name: 'ADD_CART',
        });

        // istanbul ignore next
        if (featureFlags?.enableGtmGa4) {
            trackEeAddToCart({
                items: [{
                    sku,
                    title,
                    brand: brand?.name,
                    categories,
                    isNew,
                    isClearance,
                    price: price?.price,
                    quantity: 1,
                }],
            });
        }
    };

    /* istanbul ignore next */
    const packToCart = (obj) => {
        updateCart(obj);

        track({
            type: 'ACTION',
            name: 'GENERIC_CLICK',
            action: 'Añadir a carrito',
            label: 'Click en boton Añadir al carrito',
        });

        track({
            type: 'ACTION',
            name: 'ADD_CART_BUNDLE',
            data: { product: obj.map(mapProductForTracking) },
        });

        if (featureFlags?.enableGtmGa4) {
            trackEeAddToCart({
                listName: 'product-bundled-list',
                listId: 'product-bundled-list_product',
                items: obj.reduce((p, n) => [...p, {
                    sku: n?.sku,
                    title: n?.title,
                    brand: n?.brand?.name,
                    categories,
                    isNew: n?.is_new,
                    isClearance: n?.isClearance,
                    price: n?.default_variant?.price?.price,
                    quantity: 1,
                }], []),
            });
        }
    };

    /* istanbul ignore next */
    const openPopupProductReview = () => {
        if (!user.isLogged) {
            window.dispatchEvent(new CustomEvent('login-popup:init', {
                detail: {
                    redirect: `${url}?review=1`,
                },
            }));
        } else setShowAddReview(true);
    };

    const handleClickVariant = (event, obj) => {
        switch (event) {
            /* istanbul ignore next */
            case ProductActionVariantClick.SizeModal:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver guía de tallas',
                    label: 'Click en guía de tallas',
                });
                break;
            /* istanbul ignore next */
            case ProductActionVariantClick.SizeWidget:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver guía de tallas',
                    label: 'Click en cual es mi talla',
                });
                break;
            default:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Seleccionar talla',
                    label: 'Click en talla',
                });
                fetchDeliveryTime({
                    stockLabel: obj?.stockLabel,
                    replenishment: obj?.replenishment,
                    uuid: obj?.uuid,
                });
                break;
        }
    };

    const handleClickRelated = () => track({
        type: 'ACTION',
        name: 'GENERIC_CLICK',
        action: 'Ver colores relacionados',
        label: 'Click en colores relacionados',
    });

    const handleClickWishList = (obj) => {
        track({
            type: 'ACTION',
            name: 'GENERIC_CLICK',
            action: 'Añadir a favoritos',
            label: 'Click en boton Añadir a favoritos',
        });
        // istanbul ignore next
        const event = !isWishListActive ? 'wishlist:add' : 'wishlist:del';
        /* eslint-disable-next-line no-undef */
        window.dispatchEvent(new CustomEvent(event, { detail: { id: obj.product.id } }));
        // istanbul ignore next
        if (isWishListActive) setIsWishListActive(false);
    };

    const handleClickShare = () => track({
        type: 'ACTION',
        name: 'GENERIC_CLICK',
        action: 'Compartir producto',
        label: 'Click en boton Compartir',
    });

    const handleClickWriteReviewTop = (e) => {
        track({
            type: 'ACTION',
            name: 'GENERIC_CLICK',
            action: 'Ir a dejar opinión',
            label: 'Click en dejar opinión top',
        });
        e.preventDefault();
        openPopupProductReview();
    };

    const handleClickViewReviewTop = (e) => {
        e.preventDefault();
        setTab(ProductTabType.Reviews);

        /* eslint-disable no-undef */
        window.scroll({
            top: elemTabContentWrapper.current.getBoundingClientRect().top + window.scrollY - 180,
            behavior: 'smooth',
        });
        /* eslint-enable no-undef */

        track({
            type: 'ACTION',
            name: 'GENERIC_CLICK',
            action: 'Ir a dejar opinión',
            label: 'Click en ver opiniones top',
        });
    };

    const handleClickViewReviewRelatedTop = (e) => {
        e.preventDefault();
        setTab(ProductTabType.ReviewsRelated);

        /* eslint-disable no-undef */
        window.scroll({
            top: elemTabContentWrapper.current.getBoundingClientRect().top + window.scrollY - 180,
            behavior: 'smooth',
        });
        /* eslint-enable no-undef */

        track({
            type: 'ACTION',
            name: 'GENERIC_CLICK',
            action: 'Ir a dejar opinión',
            label: 'Click en ver opiniones top',
        });

        handleClickReviewsRelated();
    };

    const handleClickWriteReviewTab = (e) => {
        track({
            type: 'ACTION',
            name: 'GENERIC_CLICK',
            action: 'Ir a dejar opinión',
            label: 'Click en dejar opinión pestañas',
        });
        e.preventDefault();
        openPopupProductReview();
    };

    const handleClickMoreReviews = () => {
        if (isProcessing) return;
        setIsProcessing(true);

        track({
            type: 'ACTION',
            name: 'GENERIC_CLICK',
            action: 'Ir a opiniones',
            label: 'Click en ver mas opiniones',
        });

        const params = `limit=${REVIEWS_PAGE_LIMIT}&page=${reviews?.pagination?.page + 1}&ids=${uuid}&lang=${lang}&country=${country.code}`;

        /* istanbul ignore next */
        /* eslint-disable-next-line no-undef */
        fetch(`${api}/api/product/reviews/?${params}`)
            /* istanbul ignore next */
            .then((ans) => ans.json())
            /* istanbul ignore next */
            .then((ans) => {
                if (ans?.data?.length && !ans.error) {
                    setReviews({
                        ...ans,
                        data: [...reviews.data, ...ans.data],
                    });
                }
                setIsProcessing(false);
            })
            /* istanbul ignore next */
            .catch(() => { });
    };

    // istanbul ignore next
    const handleClickReviewsRelated = (isShowMoreClicked) => {
        if (isShowMoreClicked) {
            track({
                type: 'ACTION',
                name: 'GENERIC_CLICK',
                action: 'Ir a opiniones',
                label: 'Click en ver mas opiniones',
            });
        }

        if (reviewsRelated?.data?.length && !isShowMoreClicked) return;
        if (isProcessing) return;

        setIsProcessing(true);

        const reviewsRelatedPage = reviewsRelated?.pagination?.page || 1;
        const params = `limit=${REVIEWS_RELATED_PAGE_LIMIT}&page=${isShowMoreClicked ? reviewsRelatedPage + 1 : reviewsRelatedPage}&ids=${relatedProductsUuids.join(':')}&lang=${lang}&country=${country.code}`;

        /* eslint-disable-next-line no-undef */
        fetch(`${api}/api/product/reviews/?${params}`)
            .then((ans) => ans.json())
            .then((ans) => {
                if (ans?.data?.length && !ans.error) {
                    setReviewsRelated({
                        ...ans,
                        data: [...(reviewsRelated?.data || []), ...ans.data],
                        pagination: {
                            ...ans.pagination,
                            totalCount: reviewsRelated?.pagination?.totalCount,
                        },
                    });
                } else if (!ans?.data?.length && !ans.error) {
                    setReviewsRelated({
                        ...reviewsRelated,
                        pagination: {
                            ...reviewsRelated.pagination,
                            totalCount: +ans?.data?.length,
                        },
                    });
                }
                setIsProcessing(false);
            })
            .catch(() => setIsProcessing(false));
    };

    const handleClickTab = (id) => {
        /* istanbul ignore next */
        if (!id) return;

        switch (id) {
            case ProductTabType.Description:
                setTab(ProductTabType.Description);
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a descripción',
                    label: 'Click en descripción',
                });
                break;

            case ProductTabType.Reviews:
                setTab(id);
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a opiniones',
                    label: 'Click en ver opiniones pestañas',
                });
                break;

            case ProductTabType.ReviewsRelated:
                setTab(id);
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a opiniones',
                    label: 'Click en ver opiniones pestañas',
                });
                handleClickReviewsRelated();
                break;

            case ProductTabType.Shipping:
                setTab(ProductTabType.Shipping);
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ir a envío y devoluciones',
                    label: 'Click en envío y devoluciones',
                });
                break;
        }
    };

    const handleGalleryClick = (e) => {
        switch (e) {
            case ProductGalleryClick.SwiperPrev:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver galeria imágenes',
                    label: 'Click en flecha anterior',
                });
                break;
            case ProductGalleryClick.SwiperNext:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver galeria imágenes',
                    label: 'Click en flecha siguiente',
                });
                break;
            case ProductGalleryClick.SwiperPagination:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver galeria imágenes',
                    label: 'Click en puntitos',
                });
                break;
            case ProductGalleryClick.Main:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver imágenes',
                    label: 'Click en imagen principal',
                });
                break;
            case ProductGalleryClick.Thumbnail:
                track({
                    type: 'ACTION',
                    name: 'GENERIC_CLICK',
                    action: 'Ver imágenes',
                    label: 'Click en imágenes miniatura',
                });
                break;
        }
    };

    /* istanbul ignore next */
    const handleClickChatBot = () => track({
        type: 'ACTION',
        name: 'GENERIC_CLICK',
        action: 'Abrir chatbot',
        label: 'Click en widget chatbot',
    });

    React.useEffect(() => {
        /* eslint-disable */
        /* istanbul ignore next */
        fetch(`${api}/promotion/product?store=${storeId}&promotion_id=1&ids=${id}`, { priority: 'low' })
            /* istanbul ignore next */
            .then(ans => ans.json())
            /* istanbul ignore next */
            .then(ans => {
                if (ans && !ans.error) setPromotion(ans.result[id]);
                setPromotionRequested(true);
            })
            /* istanbul ignore next */
            .catch(() => { });
    }, []);

    /* istanbul ignore next */
    const trackLastViewRecService = async (id, trackingId) =>
        /* eslint-disable-next-line no-undef */
        await fetch(`${api}/product/${id}/${trackingId}`, { priority: 'low' });

    /* istanbul ignore next */
    React.useEffect(() => {
        const trackingId = getCookie('dpvUid');
        if (trackingId) trackLastViewRecService(id, trackingId);
    }, [id]);

    // istanbul ignore next
    const postMessageToApp = (type, items) =>
        // eslint-disable-next-line no-undef
        window.parent.postMessage(`${type}-${items}`, /* eslint-disable no-trailing-spaces */ '*');

    /* istanbul ignore next */
    const getCart = async (e) => {
        const { sessionId } = user;
        if (!sessionId) return;
        const res = await fetch(`${api}/user/${lang}/cart?${Date.now()}`, {
            method: 'GET',
            headers: {
                sessionId: sessionId,
                country: country.code,
            },
            credentials: 'include',
            priority: 'low',
        });
        const cartRes = await res.json();
        setCartResponse(cartRes);
    };

    /* istanbul ignore next */
    const handleScrollCartContent = () => clearTimeout(cartTimeoutId);

    /* istanbul ignore next */
    const updateCart = async (obj) => {
        const itemCartSelectOption = !Array.isArray(obj)
            ? obj.selectedOption
                ? [{ selectedOption: obj.selectedOption.id, id: obj.product.id }]
                : []
            : obj.filter((p) => p.selected).map((p) => ({ selectedOption: p.selected, id: p.id }));

        if (
            Array.isArray(obj) &&
            obj.filter((p) => !p.selected && p.variants && p.variants.length).length
        ) return;

        try {
            let res = null;

            // @toDo FeatureFlag
            if (featureFlags?.enableNewAddToCart) {
                res = await fetch(`${api}/api/cart/add`, {
                    body: JSON.stringify({
                        country: country.code,
                        products:
                            !Array.isArray(obj)
                                ? [
                                    obj.selectedOption
                                        ? {
                                            productUuid: obj.selectedOption.uuid,
                                            quantity: 1,
                                        }
                                        : {productUuid: obj.product.uuid, quantity: 1},
                                ]
                                : obj.map((p) =>
                                    p.selected
                                        ? {
                                            productUuid: p.selected,
                                            quantity: 1,
                                        }
                                        : {productUuid: p.uuid, quantity: 1},
                                ),
                        }
                    ),
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                    },
                    method: 'POST',
                    credentials: 'include'
                });
            } else {
                res = await fetch(`${api}/user/cart`, {
                    body: JSON.stringify(
                        !Array.isArray(obj)
                            ? [
                                obj.selectedOption
                                    ? {
                                        product_id: obj.product.id,
                                        super_attribute: {
                                            [obj.product.variants[0].id]: obj.selectedOption.id,
                                        },
                                        qty: 1,
                                    }
                                    : {product_id: obj.product.id, qty: 1},
                            ]
                            : obj.map((p) =>
                                p.selected
                                    ? {
                                        product_id: p.id,
                                        super_attribute: {
                                            [p.variants[0].id]: p.selected,
                                        },
                                        qty: 1,
                                    }
                                    : {product_id: p.id, qty: 1},
                            ),
                    ),
                    headers: {
                        Accept: 'application/json',
                        'Content-Type': 'application/json',
                        'dpv-session': getCookie('dpv-session'),
                    },
                    method: 'POST',
                });
            }
            const cartRes = await res.json();
            const { errors } = cartRes;
            if (!errors) {
                // @toDo FeatureFlag
                if (featureFlags?.enableNewAddToCart) {
                    dispatch(SetTotalItems(cartRes.totalProductsQuantity));
                    // @toDo pending mock
                    //dispatch(SetOpenCart(true));
                } else getCart();

                if (!Array.isArray(obj)) setAddedToCart(true);
                else setBundleAddedToCart(true);

                if (!isMobile) scrollToTop();
                clearTimeout(cartButtonTimeoutId);
                setCartButtonTimeoutId(setTimeout(() => setAddedToCart(false), 3500));
            } else {
                const cartErrors = errors.map((c) => ({
                    ...c,
                    selected: (itemCartSelectOption.find(
                        (f) => f.id === c.product_id) || {}
                    )?.selectedOption,
                    quantity: 1,
                }));

                dispatch(GetCartItems({ ...cartResponse, isOpened: false }));
                setCartErrors(cartErrors);
                setShowModalAddToCart(true);
            }
        } catch (e) {
            return e;
        }
    };

    // istanbul ignore next
    React.useEffect(() => {
        if (!cartResponse) return;

        const totalItems = cartResponse?.items?.length ?
            cartResponse.items.reduce((p, { quantity }) => quantity + p, 0) : 0;

        postMessageToApp('cart', totalItems);
        dispatch(GetCartItems({ ...cartResponse, isOpened: true }));

        const handleTimeout = () => {
            setAddedToCart(false);
            dispatch(GetCartItems({ ...cartResponse, isOpened: false }));
        };

        clearTimeout(cartTimeoutId);
        setCartTimeoutId(setTimeout(handleTimeout, 4000));
        setTimeout(() => window.addEventListener(!isMobile ? 'click' : 'scroll', handleOnClickCartResponse, { once: true }));
    }, [cartResponse]);

    // istanbul ignore next
    const handleOnClickCartResponse = (e) => {
        const isScrollInside = document.getElementById('cart-display-content')?.contains(e.target);
        if (!isScrollInside) {
            dispatch(GetCartItems({
                ...cartResponse,
                isOpened: false,
            }));
        }
    };

    /* istanbul ignore next */
    const getCartErrors = (item, packs, cartErrors) => {
        const products = packs && packs.length ? packs.flat(1) : [item];
        return cartErrors.map((ce) => {
            const product = products.find(({ id }) => id === ce.product_id);
            return {
                ...product,
                error: ce.code,
                selectedOption: ce.selected,
                quantity: ce.quantity,
            };
        });
    };

    const clearPayEnabled = !outOfStock && clearPay && showClearPay(clearPay, categoryIds, price.price)

    const handleOnModalAddToCartClose =
        /* istanbul ignore next */
        () => setShowModalAddToCart(false);

    React.useEffect(() => {
        /* eslint-disable no-undef */
        window.addEventListener('cart:component-scroll', handleScrollCartContent);
        return () => {
            window.removeEventListener('cart:component-scroll', handleScrollCartContent);
            window.removeEventListener('scroll', handleOnClickCartResponse);
            window.removeEventListener('click', handleOnClickCartResponse);
        };
        /* eslint-enable no-undef */
    }, [handleScrollCartContent]);

    return (
        <div className={styles['product-page-component']}>

            <div className={styles['ads-promo-wrapper']}>
                {canRunAds ? (
                    <AdManager
                        id={AdManagerId.Promo}
                        divId={ad.promo.div_id}
                    />
                ) : (
                    !ad.promo.default_image ? null : (
                        isOptanonInited && (
                            <a href={ad?.promo.default_url || null}>
                                <Picture
                                    sizes={{
                                        360: '360x38',
                                        500: '500x53',
                                        767: '767x84',
                                        1034: '750x82',
                                        1399: '990x50',
                                    }}
                                    width={1730}
                                    height={50}
                                    src={`static/banners/adblock/promo/${ad.promo.default_image[device]}`}
                                />
                            </a>
                        )
                    )
                )}
            </div>

            <Breadcrumb
                items={breadcrumbItems}
                url={confUrl}
                onClick={() =>
                    track({
                        type: 'ACTION',
                        name: 'GENERIC_CLICK',
                        action: 'Ir a nivel superior',
                        label: 'Click en Breadcrumb',
                    })
                }
            />

            <div className={styles['product-page-wrapper']}>

                <div className={styles['product-left-wrapper']}>

                    {discountTag.show ? (
                        <div className={`${styles['product-discount']} ${/* istanbul ignore next */ gallery.images.length > 1 ? styles['left'] : ''}`}>
                            <div className={styles['bullet-product-discount']}>{'- ' + discountAmount + '%'}</div>
                        </div>
                    ) : null}

                    {brand ? <a className={styles['product-brand']} href={brand.url} title={brand.name}>
                        <Picture src={`brand/logo/${brand.image}`}
                            name={brand.name}
                            width={75}
                            height={75}
                            lazy
                        />
                    </a> : null}

                    <div className={styles['product-gallery-wrapper']}>
                        <ProductGallery images={gallery} onClick={handleGalleryClick} />
                    </div>

                    {(technologies || []).length ? (
                        <div className={styles['product-technologies']}>
                            <div className={styles['section-subtitle']}>
                                <I18n>product_page.title.technologies</I18n>
                            </div>
                            {technologies.map((technology) => (
                                <div key={technology.name} className={styles['product-technology']}>
                                    <Picture name={technology.name}
                                        src={`technology/${technology.image}.png`}
                                        lazy height={66} />
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {
                        (sustainability || []).length ? (
                            <div className={styles['product-sustainability']}>
                                <div className={styles['section-subtitle']}>
                                    <I18n>product_page.title.sustainability</I18n>
                                </div>
                                <div className={styles['sustainability-types']}>
                                    {sustainability.map((s) => (
                                        <div key={s.name} className={styles['sustainability-type']}>
                                            <Picture name={translate(s.name)} src={`/static/sustainability/${s.image}.png`} lazy width={36} height={36} /><span>{translate(s.name)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null
                    }


                </div>

                <div className={styles['product-right-wrapper']}>

                    <h1 className={styles['product-title']}>{title}</h1>

                    <div>

                        {
                            showPvpr ? (
                                <div className={styles['product-pvpr']}>
                                    <del>
                                        <I18n args={{
                                            pvp: formatPrice(
                                                price.pvp,
                                                price.currency,
                                                lang
                                            ),
                                        }}>product_page.pricing.pvp</I18n>
                                    </del>
                                </div>
                            ) : null
                        }

                        <div className={styles['product-price']}>
                            {
                                formatPrice(price.price, price.currency, lang)
                            }
                        </div>

                        {
                            showPvpr ? (
                                <div className={styles['product-pvpr']}>
                                    {
                                        /* istanbul ignore next */
                                        !showPercentDiscount ? (<I18n args={{
                                            saving: formatPrice(
                                                price.pvp - price.price,
                                                price.currency,
                                                lang
                                            ),
                                        }
                                        }>product_page.pricing.saving</I18n>)
                                            : <I18n args={{
                                                saving_percentage: discountAmount,
                                            }}>product_page.pricing.saving_percentage</I18n>
                                    }

                                </div>
                            ) : null
                        }
                    </div>

                    <div className={styles['product-summary-reviews']}>
                        {
                            rating?.count ? (
                                <div>
                                    <Stars size={'small'} rate={rating?.rate * 20} />&nbsp;
                                    <a data-testid={'product-view-review-top'} href='#' rel='nofollow' className={styles['product-view-review-top']} onClick={handleClickViewReviewTop}>
                                        <I18n args={{ reviews: rating?.count }}>
                                            {`product_page.reviews.${/* istanbul ignore next */ rating?.count > 1 ? 'view_reviews' : 'view_review'}`}
                                        </I18n>
                                    </a>
                                </div>
                            ) : (
                                <a data-testid={'product-write-review-top'} href='#' onClick={handleClickWriteReviewTop} rel='nofollow'>
                                    <I18n>product_page.reviews.reviews_first_review_alt</I18n>
                                </a>
                            )
                        }

                        {
                            reviewsRelated?.pagination?.totalCount ? (
                                <Fragment>&nbsp;|&nbsp;<a data-testid={'product-view-review-related-top'} href='#' onClick={handleClickViewReviewRelatedTop} rel='nofollow'>
                                    <I18n args={{ reviews: reviewsRelated.pagination.totalCount }}>product_page.reviews.view_related_reviews</I18n>
                                </a></Fragment>
                            ) : null
                        }

                        {
                            rating?.count ? (
                                <Fragment>&nbsp;|&nbsp;<a href='#' data-testid={'product-write-review-top'} onClick={handleClickWriteReviewTop} rel='nofollow'>
                                    <I18n>product_page.reviews.add_your_review</I18n>
                                </a></Fragment>
                            ) : null
                        }


                    </div>

                    {
                        clearPayEnabled ? (
                            <div className={`${styles['clearpay-widget-wrapper']}`}>
                                <Clearpay
                                    type={ClearpayType.Product}
                                    config={clearPay}
                                    price={price.price}
                                />
                            </div>
                        ) : null
                    }

                    {
                        !clearPayEnabled && !promotion && paypal?.widget.enabled && isValidCurrency ? (
                            <div className={`${styles['paypal-widget-wrapper']}`}>
                                {
                                    promotionRequested && /* istanbul ignore next */ <PayPalWidget amount={price.price} />
                                }
                            </div>
                        ) : null
                    }

                    <ProductActions
                        product={{ ...props.model, delivery, promotion, promotionRequested, outOfStock }}
                        isWishListActive={isWishListActive}
                        clickCart={itemToCart}
                        clickVariant={handleClickVariant}
                        clickWishList={handleClickWishList}
                        clickShare={handleClickShare}
                        clickRelated={handleClickRelated}
                        isAdded={addedToCart}
                        isValidCurrency={isValidCurrency}
                    />

                    {
                        /* istanbul ignore next */
                        showModalAddToCart && (
                            <Modal onClose={() => handleOnModalAddToCartClose(false)}>
                                <CartError
                                    items={getCartErrors(props.model, packs, cartErrors)}
                                    onClose={() => handleOnModalAddToCartClose(false)}
                                />
                            </Modal>
                        )}

                    {tags && tags.length ? (
                        <Fragment>
                            <div className={styles['section-subtitle']}>
                                <I18n>product_page.title.tags</I18n>
                            </div>
                            <TagList
                                tags={tags}
                                onClick={() =>
                                    track({
                                        type: 'ACTION',
                                        name: 'GENERIC_CLICK',
                                        action: 'Ir a nivel superior',
                                        label: 'Click en etiquetas bloque 1',
                                    })
                                }
                            />
                        </Fragment>
                    ) : null}

                    {(technologies || []).length ? (
                        <div className={styles['product-technologies']}>
                            <div className={styles['section-subtitle']}>
                                <I18n>product_page.title.technologies</I18n>
                            </div>
                            {technologies.map((technology) => (
                                <div key={technology.name} className={styles['product-technology']}>
                                    <Picture name={technology.name}
                                        src={`technology/${technology.image}.png`}
                                        lazy height={66} />
                                </div>
                            ))}
                        </div>
                    ) : null}

                    {
                        (sustainability || []).length ? (
                            <div className={styles['product-sustainability']}>
                                <div className={styles['section-subtitle']}>
                                    <I18n>product_page.title.sustainability</I18n>
                                </div>
                                <div className={styles['sustainability-types']}>
                                    {sustainability.map((s) => (
                                        <div key={s.name} className={styles['sustainability-type']}>
                                            <Picture name={translate(s.name)} src={`static/sustainability/${s.image}.png`} lazy width={36} height={36} /><span>{translate(s.name)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : null
                    }

                    {
                        isMobile && canRunAds ? (
                            <div className={styles['ads-product-middle-wrapper']}>
                                <AdManager
                                    id={AdManagerId.ProductMidMobile}
                                    divId={ad.middleMobile.div_id}
                                />
                            </div>
                        ) : null}
                </div>

            </div>
            {
                /* istanbul ignore next */
                packs && packs.length ? (
                    <Fragment>
                        <p className={styles['section-title']}>
                            <I18n>product_page.title.product_pack</I18n>
                        </p>
                        {
                            /* istanbul ignore next */
                            packs.map((b) => (
                                <BundledProducts
                                    key={`${b[0].id}-b-${b[b.length - 1].id}`}
                                    items={b}
                                    click={packToCart}
                                    isAdded={bundleAddedToCart}
                                    id={`${b[0].id}-b-${b[b.length - 1].id}`}
                                />
                            ))
                        }
                    </Fragment>
                ) : null
            }
            <div className={styles['tab-content-wrapper']} ref={elemTabContentWrapper}>
                <ProductPageTabs
                    product={{
                        description, sku, bikeMount, nonReturnable,
                        expertOpinion, reviews, reviewsRelated,
                    }}
                    activeTab={tab}
                    clickTab={handleClickTab}
                    clickAddReview={handleClickWriteReviewTab}
                    clickMoreReviews={handleClickMoreReviews}
                    clickMoreReviewsRelated={() => handleClickReviewsRelated(true)}
                />
            </div>

            {
                isMobile || !canRunAds ? null : (
                    <div className={styles['ads-product-middle-wrapper']}>
                        <AdManager
                            id={AdManagerId.ProductMid}
                            divId={ad.middle.div_id}
                        />
                    </div>
                )
            }

            {
                blocks.productAffinityProducts && blocks.productAffinityProducts.items ? (
                    <div
                        id='product-slider-pa'
                        className={`${styles['product-slider-top-wrapper']} ${styles['hidden-product-affinity']}`}
                        data-testid='elem-ps-recomm-slider'
                    >
                        <LazyLoad>
                            <ProductSlider
                                breakpoints={SliderBreakpoints}
                                title={translate(blocks.productAffinityProducts.translation)}
                                anchor
                                onClick={() =>
                                    track({
                                        type: 'ACTION',
                                        name: 'GENERIC_CLICK',
                                        action: 'Ir a Producto en Slide 1',
                                        label: 'Click en Producto de Slide',
                                    })
                                }
                                items={blocks.productAffinityProducts.items.map((i) => ({
                                    ...i,
                                    related_products: null,
                                }))}
                            />
                        </LazyLoad>
                    </div>
                ) : null
            }

            {
                /* istanbul ignore next */
                blockBrandProducts && brand ? (
                    <div className={styles['product-slider-top-wrapper']}>
                        <LazyLoad>
                            <ProductSlider
                                breakpoints={SliderBreakpoints}
                                title={translate(
                                    blockBrandProducts.translation,
                                    { brand: brand.name }
                                )}
                                anchor
                                onClick={() =>
                                    track({
                                        type: 'ACTION',
                                        name: 'GENERIC_CLICK',
                                        action: 'Ir a Producto en Slide 2',
                                        label: 'Click en Producto de Slide',
                                    })
                                }
                                items={blockBrandProducts.items.map(i => ({
                                    ...i,
                                    related_products: null,
                                }))}
                            />
                        </LazyLoad>
                    </div>
                ) : null
            }

            {blocks.categoryTopSellers && blocks.categoryTopSellers.items ? (
                <div className={styles['product-slider-top-wrapper']}>
                    <LazyLoad>
                        <ProductSlider
                            breakpoints={SliderBreakpoints}
                            title={translate(
                                blocks.categoryTopSellers.translation,
                                { category: mainCategory.name }
                            )}
                            anchor
                            onClick={() =>
                                track({
                                    type: 'ACTION',
                                    name: 'GENERIC_CLICK',
                                    action: 'Ir a Producto en Slide 3',
                                    label: 'Click en Producto de Slide',
                                })
                            }
                            items={blocks.categoryTopSellers.items.map(i => ({
                                ...i,
                                related_products: null,
                            }))}
                        />
                    </LazyLoad>
                </div>
            ) : null}

            {blocks.brand_top_sellers && brand ? (
                <div className={styles['product-slider-top-wrapper']}>
                    <LazyLoad>
                        <ProductSlider
                            breakpoints={SliderBreakpoints}
                            title={translate(
                                blocks.brand_top_sellers.translation,
                                { brand: brand.name }
                            )}
                            anchor
                            onClick={() =>
                                track({
                                    type: 'ACTION',
                                    name: 'GENERIC_CLICK',
                                    action: 'Ir a Producto en Slide 4',
                                    label: 'Click en Producto de Slide',
                                })
                            }
                            items={blocks.brand_top_sellers.items.map(i => ({
                                ...i,
                                related_products: null
                            }
                            ))}
                        />
                    </LazyLoad>
                </div>
            ) : null}

            {
                similarBrands && similarBrands.length ? (
                    <Fragment>
                        <div className={`${styles['section-title']} ${styles['link-list-title']}`}>
                            <I18n
                                args={{
                                    category: breadcrumbItems[1]?.name,
                                }}
                            >
                                product_page.title.more_brands
                            </I18n>
                        </div>
                        <LinkList
                            list={similarBrands}
                            onClick={() =>
                                track({
                                    type: 'ACTION',
                                    name: 'GENERIC_CLICK',
                                    action: 'Ir a marcas similares',
                                    label: 'Click en marcas similares',
                                })
                            }
                        />
                    </Fragment>
                ) : null
            }

            {
                /* istanbul ignore next */
                blockLastViews && blockLastViews.items ? (
                    <div
                        id='product-slider-lv'
                        className={`${styles['product-slider-top-wrapper']} ${styles['last-view-slider']}`}
                        data-testid='elem-ps-lv'
                    >
                        <LazyLoad>
                            <ProductSlider
                                breakpoints={SliderBreakpoints}
                                title={translate('generic.related.title.last_view')}
                                anchor
                                isArrowHidden={isArrowHidden()}
                                onClick={() =>
                                    track({
                                        type: 'ACTION',
                                        name: 'GENERIC_CLICK',
                                        action: 'Ir a Producto en slide last view',
                                        label: 'Click en Producto de Slide last view',
                                    })
                                }
                                items={blockLastViews.items.map(i => ({
                                    ...i,
                                    related_products: null,
                                }))}
                            />
                        </LazyLoad>
                    </div>
                ) : null
            }

            {
                !canRunAds ? (
                    !ad.footer.default_image ? null : (
                        isOptanonInited && <div className={styles['ads-footer-wrapper']}>
                            <Picture
                                width={isMobile ? 320 : 728}
                                height={isMobile ? 39 : 90}
                                src={`static/banners/adblock/footer/${ad.footer.default_image[isMobile ? DeviceType.Mobile : DeviceType.Desktop]
                                    }`}
                                lazy
                            />
                        </div>
                    )
                ) : (
                    <div className={styles['ads-footer-wrapper']}>
                        <AdManager
                            id={isMobile ? AdManagerId.FooterMobile : AdManagerId.Footer}
                            divId={ad[`footer${isMobile ? 'Mobile' : ''}`].div_id}
                        />
                    </div>
                )
            }

            {
                hubtypeWebchat && hubtypeWebchat.enabled ? (
                    <div className={styles['hubtype-webchat-wrapper']}>
                        <HubtypeWebchat {
                            ...{
                                ...hubtypeWebchat,
                                userId: user.userId,
                                email: user.email,
                            }} onClick={handleClickChatBot} />
                    </div>
                ) : (
                    null
                )
            }

            {
                /* istanbul ignore next */ showAddReview && (
                    <ReviewPopup
                        data={{
                            product: {
                                uuid,
                                name: title,
                                image: gallery.images[gallery.default],
                            }
                        }}
                        type={ReviewPopupType.Customer}
                        onClose={() => setShowAddReview(false)}
                    />
                )
            }

            {
                /* istanbul ignore next */
                outOfStockModal && (
                    <OutOfStockModal
                        items={outOfStockModal}
                        onClose={() => setOutOfStockModal(null)}
                    />
                )
            }

        </div>
    );
};
ProductPage.propTypes = {
    model: PropTypes.object,
};

export default ProductPage;
